import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export interface DelegatedTemplatePermission {
  id: number;
  scope_type: 'Organization' | 'Home';
  scope_id: number;
  granted_by: number;
  created_at: string;
}

export function useTemplatePermissions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch all delegated template permissions
  const {
    data: permissions = [],
    isLoading: isLoadingPermissions,
    error: permissionsError
  } = useQuery<DelegatedTemplatePermission[]>({
    queryKey: ['/api/template-permissions'],
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Grant permission to organization or home
  const grantPermissionMutation = useMutation({
    mutationFn: async ({ scope_type, scope_id }: { scope_type: 'Organization' | 'Home', scope_id: number }) => {
      const response = await apiRequest('POST', '/api/template-permissions', { scope_type, scope_id });
      return await response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/template-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/permissions/template-create'] });
      toast({
        title: "Permission Granted",
        description: `Template creation permission granted to ${variables.scope_type.toLowerCase()}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Grant Permission",
        description: error.message || "An error occurred while granting permission.",
        variant: "destructive",
      });
    },
  });

  // Revoke permission from organization or home
  const revokePermissionMutation = useMutation({
    mutationFn: async ({ scope_type, scope_id }: { scope_type: 'Organization' | 'Home', scope_id: number }) => {
      const response = await apiRequest('DELETE', `/api/template-permissions/${scope_type}/${scope_id}`);
      return await response.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/template-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/permissions/template-create'] });
      toast({
        title: "Permission Revoked",
        description: `Template creation permission revoked from ${variables.scope_type.toLowerCase()}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Revoke Permission",
        description: error.message || "An error occurred while revoking permission.",
        variant: "destructive",
      });
    },
  });

  // Check if a specific scope has permission
  const hasPermission = (scope_type: 'Organization' | 'Home', scope_id: number): boolean => {
    return permissions.some(
      (p: DelegatedTemplatePermission) => p.scope_type === scope_type && p.scope_id === scope_id
    );
  };

  return {
    permissions,
    isLoadingPermissions,
    permissionsError,
    grantPermission: grantPermissionMutation.mutate,
    revokePermission: revokePermissionMutation.mutate,
    isGrantingPermission: grantPermissionMutation.isPending,
    isRevokingPermission: revokePermissionMutation.isPending,
    hasPermission,
  };
}

// Hook for checking current user's template create permission
export function useCanCreateTemplates() {
  return useQuery({
    queryKey: ['/api/permissions/template-create'],
    select: (data: any) => data?.hasPermission || false,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}